import { useEffect, useState } from 'react';

/**
 * Dev force-pass gate (§5.3) — the anti-stuck mechanism for test traversal.
 * Never shown to real trainees.
 *
 * `?devPass=1` on any dashboard URL seeds it ON (persisted to localStorage);
 * `?devPass=0` clears it. Mirrors `useDevTabsAccess` / `useOctiV2Access`;
 * self-contained (no redux, no backend).
 *
 * MUST be consulted from `TraineeDashboard/index.tsx` — i.e. the top of the
 * dashboard tree — not only from the Development Plan view. Two navigations
 * eat the query string before that view ever mounts:
 *   1. a hard load of `/dashboard/development-plan?devPass=1` renders before
 *      klasses are fetched, so `canSeeDevplan` is still false and the route
 *      guard's `<Redirect to={path} />` bounces to `/dashboard`, and
 *   2. the Header's `<Link to="/dashboard/development-plan">` carries no query.
 * Resolving here — during the dashboard's first render, ahead of the redirect —
 * persists the flag before either can drop it.
 */
const STORAGE_KEY = 'vega.dev.devPass';
const PARAM = 'devPass';

const readStored = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const resolveInitial = (): boolean => {
  try {
    const value = new URLSearchParams(window.location.search).get(PARAM);
    if (value === '0' || value === 'false') {
      window.localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    if (value !== null) {
      window.localStorage.setItem(STORAGE_KEY, '1');
      return true;
    }
  } catch {
    /* ignore quota / privacy-mode failures — in-memory state still resolves */
  }
  return readStored();
};

export function useDevPassAccess(): boolean {
  const [enabled, setEnabled] = useState<boolean>(resolveInitial);

  useEffect(() => {
    // Keep multiple mounts / other tabs in sync if the flag is cleared.
    const sync = () => setEnabled(readStored());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  return enabled;
}

export default useDevPassAccess;
